import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, Plus } from "lucide-react";
import AddToListDialog from "./AddToListDialog";
import FavoriteButton from "./FavoriteButton";

export default function AlbumCard({ album, showRating = false, rating = null, size = "default", compact = false }) {
  const [showAddToList, setShowAddToList] = useState(false);
  const navigate = useNavigate();
  
  const sizeClasses = {
    small: "w-32",
    default: "w-full",
    large: "w-64",
  };

  function getAlbumLink() {
    if (album.deezer_id) {
      return "/album/deezer_" + album.deezer_id;
    }
    if (album.discogs_id) {
      return "/album/discogs_" + album.discogs_id;
    }
    return "/album/" + album.album_id;
  }

  function getCoverUrl() {
    return album.cover_xl || album.cover_big || album.cover_medium || album.cover_url || album.cover_small || "";
  }

  function handleImageError(e) {
    e.target.src = "https://placehold.co/300x300/1a1a2e/9333ea?text=No+Cover";
  }

  function handleAddToList(e) {
    e.preventDefault();
    e.stopPropagation();
    setShowAddToList(true);
  }

  if (compact) {
    return (
      <>
        <div className="group relative album-card">
          <Link to={getAlbumLink()} className="block">
            <div className="relative aspect-square rounded-lg overflow-hidden bg-white/5">
              <img
                src={getCoverUrl()}
                alt={album.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                onError={handleImageError}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300" />
              
              {/* Favorite button on hover */}
              <FavoriteButton
                albumId={album.album_id || album.deezer_id || album.discogs_id}
                size="small"
                className="absolute top-2 left-2 opacity-0 group-hover:opacity-100"
              />
              
              {/* Add to list button on hover */}
              <button
                onClick={handleAddToList}
                className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-purple-500 hover:scale-110"
                title="Add to list"
                data-testid={"add-to-list-" + album.album_id}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            
            <div className="mt-2 space-y-0.5">
              <h3 className="font-medium text-sm text-white truncate group-hover:text-purple-400 transition-colors duration-200">
                {album.title}
              </h3>
              <p 
                className="text-xs text-white/50 truncate hover:text-purple-400 cursor-pointer transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/discover?search=${encodeURIComponent(album.artist)}`);
                }}
                title={`View more by ${album.artist}`}
              >
                {album.artist}
              </p>
            </div>
          </Link>
        </div>
        
        <AddToListDialog 
          album={album} 
          open={showAddToList} 
          onOpenChange={setShowAddToList} 
        />
      </>
    );
  }

  return (
    <>
      <div className={"group relative " + sizeClasses[size] + " album-card"}>
        <Link to={getAlbumLink()} className="block" data-testid={"album-card-" + album.album_id}>
          <div className="relative aspect-square rounded-lg overflow-hidden bg-white/5">
            <img
              src={getCoverUrl()}
              alt={album.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              onError={handleImageError}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300" />
            
            {/* Favorite button on hover */}
            <FavoriteButton
              albumId={album.album_id || album.deezer_id || album.discogs_id}
              size="small"
              className="absolute top-2 left-2 opacity-0 group-hover:opacity-100"
            />
            
            {/* Add to list button on hover */}
            <button
              onClick={handleAddToList}
              className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-purple-500 hover:scale-110"
              title="Add to list"
              data-testid={"add-to-list-" + album.album_id}
            >
              <Plus className="w-4 h-4" />
            </button>
            
            {showRating && rating && (
              <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1">
                <Star className="w-3 h-3 text-purple-400 fill-purple-400" />
                <span className="text-xs font-medium text-white">{rating}</span>
              </div>
            )}
          </div>
          
          <div className="mt-3 space-y-1">
            <h3 className="font-semibold text-sm text-white truncate group-hover:text-purple-400 transition-colors duration-200">
              {album.title}
            </h3>
            <p 
              className="text-xs text-white/60 truncate hover:text-purple-400 cursor-pointer transition-colors"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/discover?search=${encodeURIComponent(album.artist)}`);
              }}
              title={`View more by ${album.artist}`}
            >
              {album.artist}
            </p>
            {album.year && (
              <p className="text-xs text-white/40">
                {album.year}
              </p>
            )}
          </div>
        </Link>
      </div>
      
      <AddToListDialog 
        album={album} 
        open={showAddToList} 
        onOpenChange={setShowAddToList} 
      />
    </>
  );
}
